/**
 * CMMD session proof-of-possession client protocol (forge#3517).
 *
 * A CMMD session issued to a Forge client can be bound to the client's Ed25519
 * device key: the public key travels with the login (`pop_public_key`), CMMD
 * stamps its thumbprint into the token (`cnf.jkt`), and every authenticated
 * request must then carry a detached Ed25519 signature over a canonical
 * request binding (method + path + timestamp + nonce). A stolen bearer token
 * alone is unusable.
 *
 * This module is the client half of the scheme CMMD verifies in
 * `server/services/auth/session-pop-binding.ts`. The protocol deliberately
 * reuses CMMD's existing crypto shapes rather than inventing a new scheme: the
 * thumbprint is the same sha256-over-SPKI-DER base64url digest as the forge
 * device registry and the desktop relay identity, and the signature covers
 * `<purpose>.<base64url payload>`, the forge relay challenge shape, with
 * Ed25519. It is not a DPoP port: proofs are plain headers, not JWTs.
 */

const HEADER_KEY = "x-cmmd-pop-key";
const HEADER_INPUT = "x-cmmd-pop-input";
const HEADER_SIGNATURE = "x-cmmd-pop-signature";

export const CMMD_SESSION_POP_PURPOSE = "cmmd-session-pop-v1";
export const CMMD_SESSION_POP_ALGORITHM = "Ed25519";
export const CMMD_SESSION_POP_KEY_HEADER = HEADER_KEY;
export const CMMD_SESSION_POP_INPUT_HEADER = HEADER_INPUT;
export const CMMD_SESSION_POP_SIGNATURE_HEADER = HEADER_SIGNATURE;

/**
 * Same 60-second window CMMD enforces (matching the relay challenge TTL).
 * Clients sign with their own clock; deployments with sane NTP stay inside it.
 */
export const CMMD_SESSION_POP_MAX_CLOCK_SKEW_SECONDS = 60;

export interface CmmdSessionPopProofPayload {
  readonly version: 1;
  /** Uppercase HTTP method of the bound request. */
  readonly htm: string;
  /** Request path, no query string and no origin: what CMMD compares. */
  readonly htu: string;
  /** Unix seconds at signing time. */
  readonly ts: number;
  /** Single-use nonce; CMMD rejects a nonce it has already seen. */
  readonly nonce: string;
}

/**
 * Signs the canonical signing input and exposes the public key PEM CMMD needs
 * at login. Implementations must hold the private key non-exportably where the
 * platform allows (WebCrypto / secure storage).
 */
export interface CmmdSessionPopSigner {
  /** SPKI PEM of the key the session will be bound to. */
  readonly publicKeyPem: string;
  /** Sign the canonical input string, returning raw signature bytes. */
  readonly sign: (signingInput: string) => Promise<Uint8Array>;
}

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

export function buildCmmdSessionPopSigningInput(payload: CmmdSessionPopProofPayload): string {
  return `${CMMD_SESSION_POP_PURPOSE}.${encodeBase64Url(new TextEncoder().encode(JSON.stringify(payload)))}`;
}

/** Wrap raw SPKI DER bytes in PEM framing for CMMD's `pop_public_key` field. */
export function spkiDerToPem(spkiDer: Uint8Array): string {
  const base64 = encodeBase64Url(spkiDer).replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const lines = padded.match(/.{1,64}/gu) ?? [];
  // Node's PEM export ends with a newline; match it so PEMs are
  // byte-identical whichever side produced them.
  return `-----BEGIN PUBLIC KEY-----\n${lines.join("\n")}\n-----END PUBLIC KEY-----\n`;
}

function randomNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return encodeBase64Url(bytes);
}

export interface BuildCmmdSessionPopProofHeadersInput {
  readonly signer: CmmdSessionPopSigner;
  /** HTTP method; uppercased to match CMMD's comparison. */
  readonly method: string;
  /** Request path exactly as CMMD will see it (no query, no origin). */
  readonly path: string;
  /** Unix seconds; defaults to the current time. Injectable for tests. */
  readonly nowSeconds?: number;
  /** Nonce; defaults to a fresh random value. Injectable for tests. */
  readonly nonce?: string;
}

/**
 * Build the three proof headers for one request. Each call produces a fresh
 * nonce; callers must not reuse the returned headers across attempts
 * (retries included), because CMMD rejects replayed nonces.
 */
export async function buildCmmdSessionPopProofHeaders(
  input: BuildCmmdSessionPopProofHeadersInput,
): Promise<Record<string, string>> {
  const payload: CmmdSessionPopProofPayload = {
    version: 1,
    htm: input.method.toUpperCase(),
    htu: input.path,
    ts: input.nowSeconds ?? Math.floor(Date.now() / 1000),
    nonce: input.nonce ?? randomNonce(),
  };
  const signingInput = buildCmmdSessionPopSigningInput(payload);
  const signature = await input.signer.sign(signingInput);
  const spkiDer = decodePemSpkiDer(input.signer.publicKeyPem);
  return {
    [HEADER_KEY]: encodeBase64Url(spkiDer),
    [HEADER_INPUT]: encodeBase64Url(new TextEncoder().encode(JSON.stringify(payload))),
    [HEADER_SIGNATURE]: encodeBase64Url(signature),
  };
}

function decodePemSpkiDer(pem: string): Uint8Array {
  const body = pem
    .replace("-----BEGIN PUBLIC KEY-----", "")
    .replace("-----END PUBLIC KEY-----", "")
    .replace(/\s+/gu, "");
  const base64 = body.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/**
 * Signer backed by a WebCrypto Ed25519 CryptoKey. Pair it with the public
 * half's SPKI DER (available at generation time via `exportKey("spki")`).
 */
export function createWebCryptoSessionPopSigner(input: {
  readonly privateKey: CryptoKey;
  readonly publicKeySpkiDer: Uint8Array;
}): CmmdSessionPopSigner {
  return {
    publicKeyPem: spkiDerToPem(input.publicKeySpkiDer),
    async sign(signingInput: string) {
      const signature = await crypto.subtle.sign(
        "Ed25519",
        input.privateKey,
        new TextEncoder().encode(signingInput),
      );
      return new Uint8Array(signature);
    },
  };
}
